/**
 * Bollinger Bands (BB)
 *
 * Volatility bands placed above and below a simple moving average.
 * Upper/Lower bands = SMA ± (stdDev × σ).  Default: period=20, stdDev=2.
 *
 * Category: Volatility | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource, computeSMA } from "./sma";

export const bollingerIndicator: IndicatorDefinition = {
  id: "bb",
  name: "Bollinger Bands",
  shortName: "BB",
  category: "volatility",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 2, max: 500 },
    { key: "stdDev", label: "Std Deviation", type: "number", default: 2, min: 0.1, max: 10, step: 0.1 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "HL2", value: "hl2" }, { label: "HLC3", value: "hlc3" },
    ]},
  ],
  plots: [
    { key: "upper", label: "Upper", color: "#2196F3", style: "line", lineWidth: 1 },
    { key: "middle", label: "Middle", color: "#2196F3", style: "line", lineWidth: 1 },
    { key: "lower", label: "Lower", color: "#2196F3", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, stdDev, source = "close" } = params;
    const src = getSource(candles, source);
    const sma = computeSMA(src, period);

    const upper: { time: number; value: number }[] = [];
    const middle: { time: number; value: number }[] = [];
    const lower: { time: number; value: number }[] = [];

    for (let i = period - 1; i < src.length; i++) {
      const mean = sma[i];
      if (isNaN(mean)) continue;

      // Standard deviation
      let sqSum = 0;
      for (let j = i - period + 1; j <= i; j++) {
        sqSum += (src[j] - mean) ** 2;
      }
      const std = Math.sqrt(sqSum / period);

      upper.push({ time: candles[i].time, value: mean + stdDev * std });
      middle.push({ time: candles[i].time, value: mean });
      lower.push({ time: candles[i].time, value: mean - stdDev * std });
    }

    return [
      { key: "upper", data: upper },
      { key: "middle", data: middle },
      { key: "lower", data: lower },
    ];
  },
};
